// Decides WHEN the briefing opens by itself: first load of the day, Orders-authorised people only.
// Also the "Remind me at 14:00" timer. Reopening from the bell goes straight through the store.
import { useEffect, useState } from 'react';
import { supabase } from '../../../supabase';
import { useNotifications } from '../../../hooks/useNotifications';
import { useUiStore } from '../../../stores/uiStore';
import { dayKey } from './briefingModel';
import { useBriefingStore, seenToday, markSeen, activeSnoozed, getRemind, setRemind } from './briefingStore';

/** Rows still worth showing: not snoozed past today. */
export function visibleRows(rows, now = new Date()) {
  const snoozed = activeSnoozed(dayKey(now));
  return rows.filter((r) => !snoozed[r.id]);
}

export function useBriefingGate() {
  const { ordersRows, isPending, ordersAllowed } = useNotifications();
  const phase = useUiStore((s) => s.entryPhase);
  const show = useBriefingStore((s) => s.show);
  const requested = useBriefingStore((s) => s.requested);
  const clearRequest = useBriefingStore((s) => s.clearRequest);
  const publish = useBriefingStore((s) => s.publish);
  const [who, setWho] = useState({ id: null, name: '' });

  useEffect(() => {
    let off = false;
    supabase.auth.getSession().then(({ data }) => {
      const u = data && data.session && data.session.user; if (off || !u) return;
      const m = u.user_metadata || {};
      const raw = m.full_name || m.name || (u.email ? u.email.split('@')[0] : '');
      const first = String(raw).split(/[\s._-]+/)[0];
      setWho({ id: u.id, name: first ? first[0].toUpperCase() + first.slice(1) : '' });
    }).catch(() => {});
    return () => { off = true; };
  }, []);

  const ready = ordersAllowed && !isPending && !!who.id && (phase === 'selection' || phase === 'app');

  // Tell the bell entry whether it may show and how many items wait (snoozed ones are not counted).
  const waiting = visibleRows(ordersRows).length;
  useEffect(() => { publish(!!ordersAllowed, waiting); }, [ordersAllowed, waiting, publish]);

  // A click on the bell entry.
  useEffect(() => {
    if (!requested) return;
    clearRequest();
    if (ready) show(visibleRows(ordersRows));
  }, [requested, ready, ordersRows, show, clearRequest]);

  // Once a day, on the first load with something to say.
  useEffect(() => {
    if (!ready) return;
    const now = new Date(); const today = dayKey(now);
    if (seenToday(who.id, today)) return;
    const rows = visibleRows(ordersRows, now);
    markSeen(who.id, today);              // seen even when quiet: one look a day, never a second pop-up
    if (rows.length) show(rows);
  }, [ready, who.id, ordersRows, show]);

  // "Remind me at 14:00": checked each minute; fires once.
  useEffect(() => {
    if (!ready) return undefined;
    const t = setInterval(() => {
      const at = getRemind(who.id);
      if (at && Date.now() >= at) { setRemind(who.id, 0); const rows = visibleRows(ordersRows); if (rows.length) show(rows); }
    }, 60000);
    return () => clearInterval(t);
  }, [ready, who.id, ordersRows, show]);

  return { name: who.name, ordersAllowed, ordersRows, userId: who.id };
}

